/* LIV product page (sections/liv-product.liquid): the image strip's thumbnails, and the price on the
   Add to cart button following the quantity, the option (for products with options) and the purchase
   option (one-time or subscription). The page works without it: the strip scrolls by swiping, and
   the button shows the one-time price of one. */
import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initGallery(panel) {
  const slides = panel.querySelector('[data-liv-slides]');
  const thumbs = [...panel.querySelectorAll('[data-liv-thumbs] [data-index]')];
  if (!slides || thumbs.length < 2) return;
  const mark = (index) => thumbs.forEach((t, i) => t.setAttribute('aria-pressed', String(i === index)));
  thumbs.forEach((thumb, i) =>
    thumb.addEventListener('click', () => {
      const slide = slides.children[i];
      if (!(slide instanceof HTMLElement)) return;
      slides.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'instant' : 'smooth' });
      mark(i);
    })
  );
  let frame = 0;
  slides.addEventListener(
    'scroll',
    () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => mark(Math.round(slides.scrollLeft / Math.max(1, slides.clientWidth))));
    },
    { passive: true }
  );
}

function initTotal(panel) {
  const { moneyFormat, currency } = panel.dataset;
  const total = panel.querySelector('[data-liv-total]');
  const price = panel.querySelector('[data-liv-price]');
  const select = panel.querySelector('[data-liv-variant]');
  const quantityInput = panel.querySelector('.quantity-selector input[name="quantity"]');
  const plans = [...panel.querySelectorAll('input[name="selling_plan"]')];
  if (!moneyFormat || !currency) return;

  // A chosen purchase option (one-time or a subscription) carries its own price
  const planPrice = () => plans.find((p) => p.checked)?.dataset.price;
  const unitPrice = () => Number(planPrice() ?? (select ? select.selectedOptions[0]?.dataset.price : panel.dataset.price)) || 0;
  const quantity = () => Math.max(1, parseInt(quantityInput?.value ?? '1', 10) || 1);
  const update = () => {
    const cents = unitPrice();
    if ((select || plans.length) && price) price.textContent = formatMoney(cents, moneyFormat, currency);
    if (total) total.textContent = ` · ${formatMoney(cents * quantity(), moneyFormat, currency)}`;
  };
  for (const p of plans) p.addEventListener('change', update);

  panel.addEventListener(ThemeEvents.quantitySelectorUpdate, update);
  quantityInput?.addEventListener('input', update);
  select?.addEventListener('change', () => {
    update();
    // Keep the address pointing at the chosen option, as Shopify product links do (?variant=)
    const url = new URL(window.location.href);
    url.searchParams.set('variant', select.value);
    history.replaceState(history.state, '', url);
  });
}

function init(root) {
  for (const panel of root.querySelectorAll('[data-liv-product]')) {
    if (panel.dataset.livReady) continue;
    panel.dataset.livReady = 'true';
    initGallery(panel);
    initTotal(panel);
  }
}

init(document);

// The theme editor re-renders a section in place when its settings change
document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof Element) init(e.target);
});
